import React, { useState, useEffect, useRef, useMemo } from 'react';
import imageCompression from 'browser-image-compression';
import { X, Save, AlertTriangle, Loader, Trash2, Info, Map as MapIcon, Check, UploadCloud, FileCog, Search, Camera, FileText, Download, WifiOff, MoreVertical, RotateCcw, ChevronDown, ChevronUp, Image as ImageIcon, ArrowLeft, Eye, Share2 } from 'lucide-react';
import StyledFileInput from '../common/StyledFileInput';
import { pdfjs } from 'react-pdf';
import { copyToClipboard } from '../../utils/clipboard';
import logoConnect10 from '../../assets/logo-connect10.png';
import PDFViewerModal from './PDFViewerModal';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useOzmapData } from '../../hooks/useOzmapData';
import { parseOzmapColor } from '../../utils/geolocationUtils';
import { STATUS } from '../../constants/appConstants';
import { getDeepseekData } from '../../services/apiService';
import { logActivity } from '../../services/activityLogService';
import { sanitizeAndValidateCoordinates } from '../../utils/coordinateUtils';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';

// Setup PDF.js worker using react-pdf's instance
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// --- FUNÇÃO AUXILIAR PARA VERIFICAR SE UM PONTO ESTÁ DENTRO DE UM POLÍGONO ---
const isPointInPolygon = (point, polygon) => {
    const lat = point.lat;
    const lng = point.lng;

    let isInside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][1], yi = polygon[i][0];
        const xj = polygon[j][1], yj = polygon[j][0];

        const intersect = ((yi > lat) !== (yj > lat))
            && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
        if (intersect) isInside = !isInside;
    }
    return isInside;
};

const ImageWithLoader = ({ primarySrc, fallbackSrc, alt, className, imgClassName = 'object-cover' }) => {
    const [loading, setLoading] = useState(true);
    const [currentSrc, setCurrentSrc] = useState(primarySrc || fallbackSrc);

    const handleError = () => {
        if (currentSrc === primarySrc && fallbackSrc) {
            setCurrentSrc(fallbackSrc);
        } else {
            setLoading(false);
        }
    };

    return (
        <div className={`relative bg-slate-200 dark:bg-slate-700 ${className}`}>
            {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <Loader className="animate-spin h-8 w-8 text-slate-500" />
                </div>
            )}
            <img
                src={currentSrc}
                alt={alt}
                className={`w-full h-full ${imgClassName} transition-opacity duration-300 ${loading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={() => setLoading(false)}
                onError={handleError}
            />
        </div>
    );
};

const resizeImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            let { width, height } = img;

            if (width > height) {
                if (width > maxWidth) {
                    height = Math.round(height * (maxWidth / width));
                    width = maxWidth;
                }
            } else {
                if (height > maxHeight) {
                    width = Math.round(width * (maxHeight / height));
                    height = maxHeight;
                }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    URL.revokeObjectURL(img.src);
                    if (blob) {
                        const newFileName = file.name.replace(/(\.[\w\d_-]+)$/i, '_thumb.jpeg');
                        resolve(new File([blob], newFileName, { type: 'image/jpeg', lastModified: Date.now() }));
                    } else {
                        reject(new Error('Falha ao criar o blob da miniatura.'));
                    }
                },
                'image/jpeg',
                0.8
            );
        };
        img.onerror = (err) => {
            URL.revokeObjectURL(img.src);
            reject(err);
        };
    });
};

const processAndWatermarkImage = (file, currentUser, usersMap, notificationAddress) => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            return reject(new Error("Geolocalização não é suportada por este navegador."));
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const { latitude, longitude } = position.coords;
                const coordsText = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

                const objectUrl = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                        URL.revokeObjectURL(objectUrl);
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');

                        const MAX_WIDTH = 1920;
                        let width = img.width;
                        let height = img.height;

                        if (width > MAX_WIDTH) {
                            height = (height * MAX_WIDTH) / width;
                            width = MAX_WIDTH;
                        }
                        canvas.width = width;
                        canvas.height = height;
                        ctx.drawImage(img, 0, 0, width, height);

                        const photoTakenBy = usersMap.get(currentUser?.uid)?.displayName || currentUser?.displayName || 'Usuário Desconhecido';
                        const dateObj = new Date();
                        const yearToDisplay = dateObj.getFullYear();
                        const dayMonthToDisplay = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
                        const timeToDisplay = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                        const addressLine = notificationAddress || 'Endereço não disponível';

                        const globeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`;
                        const globeIconImage = new Image();
                        globeIconImage.src = 'data:image/svg+xml;base64,' + btoa(globeSvg);

                        const logo = new Image();
                        logo.src = logoConnect10;

                        const drawWatermark = () => {
                            const margin = Math.max(20, Math.round(width / 60));
                            ctx.fillStyle = 'white';
                            ctx.textAlign = 'left';
                            ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
                            ctx.shadowOffsetX = 2;
                            ctx.shadowOffsetY = 2;
                            ctx.shadowBlur = 4;

                            const timeFontSize = Math.max(32, Math.round(width / 25));
                            const dateFontSize = Math.max(16, Math.round(width / 60));
                            const infoFontSize = Math.max(14, Math.round(width / 75));

                            const dateLineHeight = dateFontSize * 1.1;
                            const infoLineHeight = infoFontSize * 1.4;
                            const blockSpacing = margin * 0.5;

                            ctx.font = `bold ${timeFontSize}px Arial`;
                            const timeMetrics = ctx.measureText(timeToDisplay);
                            const separatorXCalc = margin + timeMetrics.width + (margin * 0.5);
                            const dateBlockXCalc = separatorXCalc + (margin * 0.5) + 3;
                            ctx.font = `${dateFontSize}px Arial`;
                            const dateTextWidthCalc = Math.max(ctx.measureText(yearToDisplay).width, ctx.measureText(dayMonthToDisplay).width);
                            const dynamicWidth = (dateBlockXCalc + dateTextWidthCalc) - margin;

                            let currentY = canvas.height - margin;

                            ctx.font = `${infoFontSize}px Arial`;
                            ctx.textBaseline = 'bottom';
                            ctx.fillText(addressLine, margin, currentY);
                            currentY -= infoLineHeight;

                            const globeIconSize = infoFontSize * 1.1;
                            const globeIconPadding = infoFontSize * 0.5;
                            const textCoordsX = margin + globeIconSize + globeIconPadding;
                            const globeIconY = currentY - globeIconSize - (infoFontSize * 0.1);
                            if (globeIconImage.complete && globeIconImage.naturalWidth !== 0) {
                                ctx.drawImage(globeIconImage, margin, globeIconY, globeIconSize, globeIconSize);
                            }
                            ctx.fillText(coordsText, textCoordsX, currentY);
                            currentY -= infoLineHeight;

                            ctx.fillText(photoTakenBy, margin, currentY);
                            currentY -= infoLineHeight;

                            currentY -= blockSpacing;
                            ctx.save();
                            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
                            ctx.shadowColor = 'transparent';
                            ctx.fillRect(margin, currentY, dynamicWidth, 1);
                            ctx.restore();
                            currentY -= blockSpacing;

                            const timeBlockHeight = timeFontSize;
                            const timeBlockCenterY = currentY - (timeBlockHeight / 2);

                            ctx.font = `bold ${timeFontSize}px Arial`;
                            ctx.textBaseline = 'middle';
                            ctx.fillText(timeToDisplay, margin, timeBlockCenterY);

                            const separatorX = margin + timeMetrics.width + (margin * 0.5);
                            const separatorHeight = timeFontSize * 0.8;
                            ctx.fillStyle = '#FFA500';
                            ctx.fillRect(separatorX, timeBlockCenterY - (separatorHeight / 2), 3, separatorHeight);
                            ctx.fillStyle = 'white';

                            ctx.font = `${dateFontSize}px Arial`;
                            const dateBlockX = separatorX + (margin * 0.5) + 3;
                            ctx.fillText(yearToDisplay, dateBlockX, timeBlockCenterY - (dateLineHeight / 2));
                            ctx.fillText(dayMonthToDisplay, dateBlockX, timeBlockCenterY + (dateLineHeight / 2));

                            currentY -= timeBlockHeight;

                            if (logo.complete && logo.naturalWidth !== 0) {
                                currentY -= blockSpacing;
                                let logoHeight = Math.max(40, Math.round(height / 18));
                                let logoWidth = (logo.width / logo.height) * logoHeight;

                                if (logoWidth > dynamicWidth) {
                                    logoHeight = (logoHeight * dynamicWidth) / logoWidth;
                                    logoWidth = dynamicWidth;
                                }

                                const logoY = currentY - logoHeight;
                                ctx.drawImage(logo, margin, logoY, logoWidth, logoHeight);
                            }

                            canvas.toBlob(
                                (blob) => {
                                    canvas.width = 0;
                                    canvas.height = 0;
                                    if (blob) {
                                        const newFileName = file.name.replace(/\.\w+$/, '.jpg');
                                        resolve(new File([blob], newFileName, { type: 'image/jpeg', lastModified: Date.now() }));
                                    } else {
                                        reject(new Error("Falha ao criar blob da imagem com marca d'água."));
                                    }
                                },
                                'image/jpeg',
                                0.85
                            );
                        };

                        let imagesLoaded = 0;
                        const totalImages = 2;
                        const onImageLoad = () => {
                            imagesLoaded++;
                            if (imagesLoaded === totalImages) {
                                drawWatermark();
                            }
                        };
                        logo.onload = onImageLoad;
                        globeIconImage.onload = onImageLoad;
                        logo.onerror = () => { console.warn("Logo não pôde ser carregado."); onImageLoad(); };
                        globeIconImage.onerror = () => { console.warn("Ícone de globo não pôde ser carregado."); onImageLoad(); };

                        if (logo.complete) onImageLoad();
                        if (globeIconImage.complete) onImageLoad();
                    };
                img.onerror = () => {
                    URL.revokeObjectURL(objectUrl);
                    reject(new Error("Não foi possível carregar a imagem para processamento."));
                };
                img.src = objectUrl;
            },
            (error) => { reject(error); },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 120000 }
        );
    });
};


// --- SUBCOMPONENTE PARA O MAPA OZMAP ---
const OzmapDisplay = ({ notification, darkMapEnabled, isVisible, ozmapData, isLoading, error }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const layerGroupsRef = useRef({
        regioes: L.layerGroup(),
        cabos: L.layerGroup(),
        postes: L.layerGroup(),
        caixas: L.layerGroup(),
        notificationMarker: L.layerGroup(),
    });

    useEffect(() => {
        if (isVisible && mapRef.current) {
            setTimeout(() => mapRef.current.invalidateSize(), 100);
        }
    }, [isVisible]);

    useEffect(() => {
        if (!mapContainerRef.current) return;

        if (!mapRef.current) {
            mapRef.current = L.map(mapContainerRef.current, {
                zoomSnap: 0.1,
                zoomDelta: 0.1,
                layers: Object.values(layerGroupsRef.current)
            });

            mapRef.current.createPane('regioesPane').style.zIndex = 410;
            mapRef.current.createPane('cabosPane').style.zIndex = 420;
            mapRef.current.createPane('postesPane').style.zIndex = 610;
            mapRef.current.createPane('caixasPane').style.zIndex = 620;
            mapRef.current.createPane('notificationMarkerPane').style.zIndex = 700;
        }

        mapRef.current.eachLayer(layer => {
            if (layer instanceof L.TileLayer) {
                mapRef.current.removeLayer(layer);
            }
        });
        L.tileLayer(
            getMapTileUrl(darkMapEnabled),
            {
                attribution: getMapTileAttribution(darkMapEnabled),
                maxZoom: 22,
                maxNativeZoom: 19,
            }
        ).addTo(mapRef.current);

        Object.values(layerGroupsRef.current).forEach(group => group.clearLayers());

        if (notification?.coordinates) {
            mapRef.current.setView([notification.coordinates.lat, notification.coordinates.lng], 16);

            const notificationIcon = L.divIcon({
                html: `
                    <svg width="38" height="38" viewBox="0 0 38 38" style="filter: drop-shadow(0 4px 6px rgba(0,0,0,0.4));">
                        <path fill="#ef4444" d="M19,0 C12.373,0 7,5.373 7,12 C7,21.875 19,38 19,38 S31,21.875 31,12 C31,5.373 25.627,0 19,0 Z"></path>
                        <path fill="white" d="M17.5,17h3v-6h-3V17z M19,20c-0.83,0-1.5,0.67-1.5,1.5S18.17,23,19,23s1.5-0.67,1.5-1.5S19.83,20,19,20z"></path>
                    </svg>
                `,
                className: 'bg-transparent border-0',
                iconSize: [38, 38],
                iconAnchor: [19, 38],
                popupAnchor: [0, -38]
            });

            let notificationRegionName = 'N/A';
            if (ozmapData.regioes) {
                const foundRegion = ozmapData.regioes.find(regiao =>
                    regiao.coordinates && isPointInPolygon(notification.coordinates, regiao.coordinates)
                );
                if (foundRegion) {
                    notificationRegionName = foundRegion.name;
                }
            }

            const popupContent = `
                <b>LOCAL DA NOTIFICAÇÃO</b><br>
                <b>Endereço:</b> ${notification.address || 'N/A'}<br>
                <b>Setor:</b> ${notificationRegionName}<br>
                <b>Coords:</b> ${Number(notification.coordinates.lat).toFixed(6)}, ${Number(notification.coordinates.lng).toFixed(6)}
            `;

            L.marker([notification.coordinates.lat, notification.coordinates.lng], {
                icon: notificationIcon,
                pane: 'notificationMarkerPane'
            }).bindPopup(popupContent)
                .addTo(layerGroupsRef.current.notificationMarker);


            L.circle([notification.coordinates.lat, notification.coordinates.lng], {
                radius: 500,
                color: '#3b82f6',
                fillOpacity: 0.1,
                weight: 2
            }).addTo(mapRef.current);
        }

        if (ozmapData) {
            ozmapData.regioes.forEach(regiao => {
                if (Array.isArray(regiao.coordinates) && regiao.coordinates.length > 0) {
                    const regionColor = parseOzmapColor(regiao.color);
                    L.polygon(regiao.coordinates, { color: regionColor, weight: 2, fillOpacity: 0.2, pane: 'regioesPane' })
                        .bindPopup(`<b>Região:</b> ${regiao.name || 'N/A'}`)
                        .addTo(layerGroupsRef.current.regioes);
                }
            });

            if (ozmapData.cabos) {
                ozmapData.cabos.forEach(cabo => {
                    const cableType = cabo.cableType?.code || '';
                    if (cableType.toLowerCase().includes('drop padrão')) {
                        return;
                    }

                    let coords = [];
                    const parsePoint = (pt) => {
                        if (Array.isArray(pt) && pt.length >= 2) return [pt[1], pt[0]];
                        if (pt && typeof pt === 'object' && pt.coords) return parsePoint(pt.coords);
                        return null;
                    };

                    if (cabo.boxA) {
                        const ptA = parsePoint(cabo.boxA.coords);
                        if (ptA) coords.push(ptA);
                    }

                    if (cabo.poles && Array.isArray(cabo.poles)) {
                        cabo.poles.forEach(poleEntry => {
                            if (poleEntry.id) {
                                const ptPole = parsePoint(poleEntry.id.coords);
                                if (ptPole) coords.push(ptPole);
                            }
                        });
                    }

                    if (cabo.boxB) {
                        const ptB = parsePoint(cabo.boxB.coords);
                        if (ptB) coords.push(ptB);
                    }

                    if (coords.length >= 2) {
                        let colorHex = null;
                        if (cabo.color && typeof cabo.color === 'object' && cabo.color.color) {
                            colorHex = cabo.color.color;
                        } else if (typeof cabo.color === 'string') {
                            colorHex = cabo.color;
                        }

                        if (!colorHex && cabo.cableType?.config?.regular?.color) {
                            colorHex = cabo.cableType.config.regular.color;
                        }

                        if (!colorHex) {
                            colorHex = '#000000';
                        }

                        let weight = 3;
                        if (cabo.cableType?.config?.regular?.weight) {
                            weight = cabo.cableType.config.regular.weight;
                        }

                        const cableColor = parseOzmapColor(colorHex) || '#000000';

                        L.polyline(coords, {
                            color: cableColor,
                            weight: weight,
                            opacity: 0.8,
                            pane: 'cabosPane'
                        }).bindPopup(`<b>Cabo:</b> ${cabo.name || 'N/A'}<br><b>Tipo:</b> ${cableType || 'N/A'}`)
                          .addTo(layerGroupsRef.current.cabos);
                    }
                });
            }

            ozmapData.postes.forEach(pole => {
                if (pole.lat != null && pole.lng != null) {
                    L.circleMarker([pole.lat, pole.lng], {
                        radius: 4,
                        fillColor: "#808080",
                        color: "#000",
                        weight: 1,
                        opacity: 1,
                        fillOpacity: 0.8,
                        pane: 'postesPane'
                    }).bindPopup(`<b>Poste:</b> ${pole.name || 'N/A'}`)
                        .addTo(layerGroupsRef.current.postes);
                }
            });

            ozmapData.caixas.forEach(caixa => {
                if (caixa.lat != null && caixa.lng != null) {
                    let fillColor;
                    const caixaName = (caixa.name || '').toUpperCase();

                    if (caixaName.includes('CTO')) {
                        fillColor = '#FFFF00';
                    } else if (caixaName.includes('CEO')) {
                        fillColor = '#00FFFF';
                    } else {
                        fillColor = parseOzmapColor(caixa.color) || '#808080';
                    }

                    L.circleMarker([caixa.lat, caixa.lng], {
                        radius: 9,
                        fillColor: fillColor,
                        color: fillColor,
                        weight: 2,
                        opacity: 1,
                        fillOpacity: 0.9,
                        pane: 'caixasPane'
                    }).bindPopup(`<b>Caixa:</b> ${caixa.name || 'N/A'}`)
                        .addTo(layerGroupsRef.current.caixas);
                }
            });
        }

        setTimeout(() => mapRef.current?.invalidateSize(), 100);

    }, [notification, ozmapData, darkMapEnabled]);

    if (!notification?.coordinates?.lat || !notification?.coordinates?.lng) {
        return <div className="flex items-center justify-center h-full bg-slate-100 dark:bg-slate-700/50 rounded-lg"><p className="text-slate-500">Coordenadas não disponíveis para exibir o mapa.</p></div>;
    }

    return (
        <div className="relative w-full h-[300px] md:h-[400px] rounded-lg">
            {isLoading && <div className="absolute inset-0 bg-black/50 text-white flex items-center justify-center z-10 rounded-lg"><Loader className="animate-spin mr-2" /> Carregando dados do Ozmap...</div>}
            {error && <div className="absolute inset-0 bg-red-500/80 text-white flex items-center justify-center z-10 p-4 text-center rounded-lg">Erro ao carregar dados do Ozmap: {error.message}</div>}
            <div ref={mapContainerRef} className="w-full h-full rounded-lg z-0"></div>
        </div>
    );
};


const ConfirmationDialog = ({ details, setDetails }) => {
    if (!details.isOpen) return null;

    const handleConfirm = () => {
        if (details.onConfirm) {
            details.onConfirm();
        }
        setDetails({ isOpen: false, title: '', message: '', onConfirm: null });
    };

    const handleClose = () => {
        setDetails({ isOpen: false, title: '', message: '', onConfirm: null });
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[1060] flex justify-center items-center p-4 animate-fade-in">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
                <div className="p-6 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/50">
                        <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                    </div>
                    <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-slate-100">{details.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{details.message}</p>
                </div>
                <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 flex justify-center gap-3 rounded-b-2xl">
                    <button type="button" onClick={handleClose} className="btn-secondary w-full">
                        Cancelar
                    </button>
                    <button type="button" onClick={handleConfirm} className="btn-primary bg-red-600 hover:bg-red-700 w-full">
                        Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
};

const ClosureDialog = ({ isOpen, onClose, onConfirm, isProcessing }) => {
    const [comment, setComment] = useState('');

    useEffect(() => {
        if (isOpen) {
            setComment('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 z-[1070] flex justify-center items-center p-4 animate-fade-in">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-md animate-in fade-in zoom-in-95 duration-150" onClick={e => e.stopPropagation()}>
                <div className="p-6">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2">Encerrar Notificação</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                        Deseja adicionar observações finais antes de encerrar? Se não desejar, basta deixar em branco e clicar em "Encerrar".
                    </p>
                    <textarea
                        className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                        rows="4"
                        placeholder="Adicione seus comentários aqui (opcional)..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        autoFocus
                    ></textarea>
                </div>
                <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 rounded-b-2xl">
                    <button type="button" onClick={onClose} className="btn-secondary" disabled={isProcessing}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={() => onConfirm(comment.trim() || null)}
                        className="btn-primary"
                        disabled={isProcessing}
                    >
                        {isProcessing ? <Loader className="animate-spin" size={18} /> : 'Encerrar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const NewNotificationModal = ({
    isOpen,
    onClose,
    onSave,
    onUpdate,
    onDelete,
    notification,
    cities,
    isProcessing: isSaving,
    userRole,
    usersList,
    theme,
    settings,
    onAssign,
    currentUser,
    onSavePhoto,
    onDeletePhoto,
    onCloseNotification,
    onGenerateReport,
    setModalMessage,
    onFileDownload,
    onAbandon,
    onMarkAsAnswered,
    onRestore, // Nova prop para restaurar
}) => {
    const emptyForm = useMemo(() => ({
        notificationNumber: '',
        assignedTechnician: '',
        city: cities.length > 0 ? cities[0].nome : '',
        neighborhood: '',
        address: '',
        cep: '',
        poleQuantity: 0,
        coordinates: { lat: '', lng: '' },
        dueDate: '',
        status: STATUS.PENDENTE,
        description: '',
        issueDate: '',
        deadlineDays: '',
        origin: '',
        irregularities: '',
    }), [cities]);

    const [formData, setFormData] = useState(emptyForm);
    const [files, setFiles] = useState([]);
    const [removedFiles, setRemovedFiles] = useState([]);
    const [isExtracting, setIsExtracting] = useState(false);
    const [isSearchingCep, setIsSearchingCep] = useState(false);
    const [isTakingPhoto, setIsTakingPhoto] = useState(false);
    const [photoProgressMsg, setPhotoProgressMsg] = useState('');
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [confirmationDetails, setConfirmationDetails] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [zoomedPhoto, setZoomedPhoto] = useState(null);
    const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
    const [isNetworkless, setIsNetworkless] = useState(false);
    const [pdfToView, setPdfToView] = useState(null);
    const [isMarkingAnswered, setIsMarkingAnswered] = useState(false);

    // Novos estados para a reformulação
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showClosureDialog, setShowClosureDialog] = useState(false);
    const [expandedSections, setExpandedSections] = useState({ data: true, map: false }); // Accordion state for mobile

    const photoInputRef = useRef(null);
    const menuRef = useRef(null);

    const { ozmapData, isLoading: ozmapLoading, error: ozmapError } = useOzmapData({
        projectName: formData.city ? formData.city.toUpperCase() : '',
        centerCoordinates: formData.coordinates,
        radius: 500
    });

    const canEdit = userRole === 'administrador' || userRole === 'tecnico';
    const isAssignedToCurrentUser = (notification && notification.assignedTechnician === currentUser?.uid) || (formData.assignedTechnician === currentUser?.uid);
    const canManagePhotos = isAssignedToCurrentUser || userRole === 'administrador' || userRole === 'tecnico';
    const isReadOnly = notification ? !canEdit : false;
    const isMapDark = theme === 'dark' && settings?.darkMapEnabled;
    const isProcessing = isSaving || isExtracting;


    // Fechar menu ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleDownloadClick = async (file, downloadKey) => {
        if (!file.path && !file.url) return;
        const isPdf = file.name && file.name.toLowerCase().endsWith('.pdf');
        if (isPdf) {
            setPdfToView(file);
            setIsPdfViewerOpen(true);
        } else {
            if (downloading) return;
            setDownloading(downloadKey);

            let downloadName = file.name;
            if (downloadKey.startsWith('photo-')) {
                const timestampStr = file.createdAt?.toDate
                    ? file.createdAt.toDate().toISOString().replace(/[-:]/g, '').replace('T', 'T').split('.')[0]
                    : (file.createdAt
                        ? new Date(file.createdAt).toISOString().replace(/[-:]/g, '').replace('T', 'T').split('.')[0]
                        : Math.random().toString(36).substring(2, 10));

                const notifNumber = formData.notificationNumber || 'UNKNOWN';
                const formattedNotifNumber = notifNumber.replace(/[\s\/\-]/g, '_');

                const originalName = file.name || 'photo.jpg';
                const extension = originalName.split('.').pop() || 'jpg';

                downloadName = `${formattedNotifNumber}-${timestampStr}.${extension}`;
            }

            await onFileDownload(file.path, downloadName);
            setDownloading(null);
        }
    };

    const handleAssign = async () => {
        if (notification?.id && currentUser) {
            const success = await onAssign(notification.id, currentUser);
            if (success) {
                setFormData(prev => ({
                    ...prev,
                    assignedTechnician: currentUser.uid,
                    status: 'em_execucao'
                }));
            }
        }
    };

    const handleGeneratePdf = async () => {
        if (!formData.photos || formData.photos.length === 0) return;
        if (formData.notificationNumber) {
            await copyToClipboard(formData.notificationNumber);
            setModalMessage("Número da notificação copiado para a área de transferência!");
        }
        setIsGeneratingPdf(true);
        try {
            const usersMap = new Map();
            usersList.forEach(user => usersMap.set(user.id, user));
            await onGenerateReport(formData, formData.photos, usersMap);
        } catch (error) {
            console.error("Erro ao solicitar geração de PDF:", error);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleMarkAsAnswered = async () => {
        if (!notification?.id) return;
        setIsMarkingAnswered(true);
        try {
            await copyToClipboard(formData.notificationNumber);
            await onMarkAsAnswered(notification.id);
        } catch (error) {
            console.error("Erro ao marcar como respondida:", error);
            setModalMessage("Ocorreu um erro ao tentar marcar a notificação como respondida.");
        } finally {
            setIsMarkingAnswered(false);
        }
    };

    const handlePhotoCapture = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsTakingPhoto(true);
        setPhotoProgressMsg('Preparando imagem...');
        try {
            const deviceRam = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 4;
            const maxWidthOrHeight = deviceRam <= 2 ? 1280 : 1920;

            setPhotoProgressMsg('Comprimindo imagem...');
            const preparedFile = await imageCompression(file, {
                maxWidthOrHeight,
                useWebWorker: true,
                fileType: 'image/jpeg',
                initialQuality: 0.92,
            });

            setPhotoProgressMsg('Obtendo localização GPS...');
            const usersMap = new Map();
            usersList.forEach(user => usersMap.set(user.id, user));

            const notificationAddress = formData.address;

            setPhotoProgressMsg('Aplicando marca d\'água...');
            const processedPhoto = await processAndWatermarkImage(preparedFile, currentUser, usersMap, notificationAddress);

            setPhotoProgressMsg('Gerando miniatura...');
            const thumbnailPhoto = await resizeImage(processedPhoto, 400, 400);

            setPhotoProgressMsg('Enviando foto...');
            await onSavePhoto(notification.id, processedPhoto, thumbnailPhoto);

        } catch (error) {
            console.error("Erro ao capturar ou processar foto:", error);
            let message = "Não foi possível adicionar a foto. " + (error.message || '');
            if (error.code) {
                switch (error.code) {
                    case 1: message = "Permissão de localização negada. A geolocalização é necessária para a marca d'água."; break;
                    case 2: message = "Não foi possível obter a localização atual. Verifique o sinal de GPS ou a conexão."; break;
                    case 3: message = "Tempo esgotado ao obter a localização. Tente novamente."; break;
                    default: message = `Erro de localização desconhecido (código: ${error.code}).`;
                }
            }
            setModalMessage(message);
        } finally {
            setIsTakingPhoto(false);
            setPhotoProgressMsg('');
            if (photoInputRef.current) {
                photoInputRef.current.value = "";
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            if (notification) {
                logActivity('view', 'notification', notification.id, null, notification.notificationNumber);
                setFormData({
                    ...emptyForm,
                    ...notification,
                    coordinates: notification.coordinates || { lat: '', lng: '' },
                });
                setIsNetworkless(notification.isNetworkless || false);
            } else {
                setFormData(emptyForm);
                setIsNetworkless(false);
            }
            setFiles([]);
            setRemovedFiles([]);
            setIsExtracting(false);
            setIsSearchingCep(false);
            // Expand all sections on desktop on init, specific on mobile
            setExpandedSections({ data: true, map: false });
        }
    }, [notification, isOpen, cities, emptyForm]);

    const handleCepSearch = () => {
        const cep = formData.cep?.replace(/\D/g, '');
        if (cep?.length === 8) {
            setIsSearchingCep(true);
            fetch(`https://viacep.com.br/ws/${cep}/json/`)
                .then(res => res.json())
                .then(data => {
                    if (!data.erro) {
                        setFormData(prev => ({
                            ...prev,
                            address: data.logradouro,
                            neighborhood: data.bairro,
                            city: data.localidade,
                        }));
                    }
                })
                .catch(error => {
                    console.error("Erro ao buscar CEP:", error);
                })
                .finally(() => {
                    setIsSearchingCep(false);
                });
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleFileChange = (event) => {
        const newFiles = event.target.files;
        if (newFiles) {
            setFiles(Array.from(newFiles));
        }
    };

    const handleExtractData = async () => {
        if (files.length === 0) return;
        const pdfFile = files.find(file => file.type === 'application/pdf');
        if (!pdfFile) {
            setModalMessage("Nenhum arquivo PDF encontrado para extrair dados.");
            return;
        }

        setIsExtracting(true);
        setModalMessage("Extraindo texto do PDF...");

        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const pdf = await pdfjs.getDocument(arrayBuffer).promise;
            let fullText = '';
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const textContent = await page.getTextContent();
                fullText += textContent.items.map(item => item.str).join(' ');
            }

            setModalMessage("Analisando documento com Inteligência Artificial...");
            const extractedData = await getDeepseekData(fullText);

            if (extractedData.issueDate && extractedData.deadlineDays) {
                const deadline = parseInt(extractedData.deadlineDays, 10);
                if (!isNaN(deadline)) {
                    const parts = extractedData.issueDate.split('-');
                    if (parts.length === 3) {
                        const year = parseInt(parts[0], 10);
                        const month = parseInt(parts[1], 10) - 1;
                        const day = parseInt(parts[2], 10);
                        const issueDateObj = new Date(Date.UTC(year, month, day));
                        if (!isNaN(issueDateObj.getTime())) {
                            issueDateObj.setUTCDate(issueDateObj.getUTCDate() + deadline);
                            extractedData.dueDate = issueDateObj.toISOString().split('T')[0];
                        }
                    }
                }
            }

            Object.keys(extractedData).forEach(key => {
                if (extractedData[key] === null || extractedData[key] === undefined) {
                    delete extractedData[key];
                }
            });

            if (extractedData.coordinates) {
                if (extractedData.coordinates.lat) extractedData.coordinates.lat = String(extractedData.coordinates.lat).replace(/,/g, '.');
                if (extractedData.coordinates.lng) extractedData.coordinates.lng = String(extractedData.coordinates.lng).replace(/,/g, '.');
            }

            setFormData(prev => ({ ...prev, ...extractedData }));
            setModalMessage("Dados extraídos com sucesso!");

        } catch (error) {
            console.error("Erro ao extrair dados via API:", error);
            setModalMessage(`Falha na extração: ${error.message}`);
        } finally {
            setIsExtracting(false);
        }
    };

    const handleRemoveAttachment = (fileToRemove) => {
        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter(f => f.path !== fileToRemove.path)
        }));
        setRemovedFiles(prev => [...prev, fileToRemove]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validation = sanitizeAndValidateCoordinates(formData.coordinates);
        if (!validation.isValid) {
            setModalMessage(validation.error);
            return;
        }
        const sanitizedFormData = { ...formData, coordinates: validation.sanitized };
        if (notification?.id) {
            onUpdate(notification.id, sanitizedFormData, files, removedFiles);
        } else {
            onSave(sanitizedFormData, files);
        }
    };

    const handleClosureConfirm = (comments) => {
        if (!isNetworkless && (!formData.photos || formData.photos.length === 0)) {
            setModalMessage("É necessário adicionar pelo menos uma foto ou marcar como 'Rede Ausente' antes de encerrar.");
            setShowClosureDialog(false);
            return;
        }
        onCloseNotification(notification, isNetworkless, comments);
        setShowClosureDialog(false);
    };

    const handleRestoreConfirm = () => {
        onRestore(notification.id);
    };

    if (!isOpen) return null;

    const labelStyle = `block text-sm font-semibold mb-2 ${isReadOnly ? 'text-slate-500' : 'text-slate-600 dark:text-slate-300'}`;
    const inputStyle = `input-style w-full px-3 py-2 ${isReadOnly ? 'bg-slate-100 dark:bg-slate-700/50 cursor-not-allowed' : ''}`;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1050] flex justify-center items-center p-0 md:p-4 animate-fade-in">
            <PDFViewerModal
                isOpen={isPdfViewerOpen}
                onClose={() => setIsPdfViewerOpen(false)}
                onDownload={() => onFileDownload(pdfToView.path, pdfToView.name)}
                fileUrl={pdfToView?.url || pdfToView?.pdfURL}
                fileName={pdfToView?.name || pdfToView?.pdfFileName}
            />

            <ConfirmationDialog
                details={confirmationDetails}
                setDetails={setConfirmationDetails}
            />

            <ClosureDialog
                isOpen={showClosureDialog}
                onClose={() => setShowClosureDialog(false)}
                onConfirm={handleClosureConfirm}
                isProcessing={isProcessing}
            />

            <div className="bg-white dark:bg-slate-800 md:rounded-2xl shadow-2xl w-full max-w-7xl flex flex-col h-full md:h-[90vh] overflow-hidden" role="dialog" aria-modal="true">

                {/* --- HEADER --- */}
                <header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-800 z-30">
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="md:hidden p-2 -ml-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700">
                            <ArrowLeft size={20} />
                        </button>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 truncate max-w-[200px] md:max-w-none">
                            {notification ? (notification.notificationNumber ? `Notificação ${notification.notificationNumber}` : 'Editar Notificação') : 'Nova Notificação'}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {canEdit && (
                            <button onClick={handleSubmit} disabled={isProcessing} className="hidden md:flex btn-primary items-center py-1.5 px-3 text-sm">
                                {isProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                                Salvar
                            </button>
                        )}

                        {/* CONTEXT MENU */}
                        {notification?.id && (
                            <div className="relative" ref={menuRef}>
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                    aria-label="Mais opções"
                                    aria-expanded={isMenuOpen}
                                >
                                    <MoreVertical size={20} />
                                </button>
                                {isMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                                        <button
                                            onClick={async () => {
                                                setIsMenuOpen(false);
                                                const shareableLink = `${window.location.origin}/?notificationId=${notification.id}`;
                                                const success = await copyToClipboard(shareableLink);
                                                if (success) {
                                                    setModalMessage("Link de compartilhamento copiado para a área de transferência!");
                                                } else {
                                                    setModalMessage("Não foi possível copiar o link.");
                                                }
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center"
                                        >
                                            <Share2 size={16} className="mr-2 text-green-500" /> Compartilhar
                                        </button>
                                        {notification.status === STATUS.FINALIZADA && (userRole === 'administrador' || userRole === 'tecnico') && (
                                            <button
                                                onClick={() => {
                                                    setIsMenuOpen(false);
                                                    setConfirmationDetails({
                                                        isOpen: true,
                                                        title: 'Restaurar Notificação',
                                                        message: 'Deseja reabrir esta notificação? Ela voltará para o status "Pendente" e a atribuição de técnico será removida.',
                                                        onConfirm: handleRestoreConfirm
                                                    });
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center"
                                            >
                                                <RotateCcw size={16} className="mr-2 text-blue-500" /> Restaurar
                                            </button>
                                        )}
                                        {isAssignedToCurrentUser && (
                                            <button
                                                onClick={() => {
                                                    setIsMenuOpen(false);
                                                    setConfirmationDetails({
                                                        isOpen: true,
                                                        title: 'Abandonar Notificação',
                                                        message: 'Você tem certeza que deseja abandonar esta notificação? Ela voltará para a lista de pendentes.',
                                                        onConfirm: () => onAbandon(notification.id)
                                                    });
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center"
                                            >
                                                <X size={16} className="mr-2 text-orange-500" /> Abandonar
                                            </button>
                                        )}
                                        {canEdit && (
                                            <button
                                                onClick={() => {
                                                    setIsMenuOpen(false);
                                                    setConfirmationDetails({
                                                        isOpen: true,
                                                        title: 'Excluir Notificação',
                                                        message: 'Você tem certeza que deseja excluir permanentemente esta notificação?',
                                                        onConfirm: () => onDelete(notification.id)
                                                    });
                                                }}
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                                            >
                                                <Trash2 size={16} className="mr-2" /> Excluir
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors hidden md:block">
                            <X size={24} />
                        </button>
                    </div>
                </header>

                {/* --- MAIN CONTENT (SPLIT VIEW) --- */}
                <div className="flex-1 overflow-y-auto md:overflow-hidden flex flex-col md:grid md:grid-cols-2 relative">

                    {/* --- COLUMN 1: DATA FORM (LEFT) --- */}
                    <div className="p-4 md:p-6 md:border-r md:border-slate-200 dark:md:border-slate-700 space-y-6 md:h-full md:overflow-y-auto pb-4 md:pb-6">

                        {/* Mobile Accordion Header for Data */}
                        <div
                            className="md:hidden flex justify-between items-center bg-slate-100 dark:bg-slate-700/50 p-3 rounded-lg cursor-pointer"
                            onClick={() => toggleSection('data')}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedSections.data}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSection('data')}
                        >
                            <h3 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2"><Info size={18} /> Dados da Notificação</h3>
                            {expandedSections.data ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>

                        {/* Form Content (Collapsible on Mobile) */}
                        <div className={`${!expandedSections.data && 'hidden'} md:block space-y-6`}>
                            {/* File Upload Section */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg space-y-3">
                                {!notification ? (
                                    <>
                                        <StyledFileInput
                                            label="Anexar arquivos (PDF para extração)"
                                            onChange={handleFileChange}
                                            accept=".pdf,.dwg,.kml,.kmz"
                                            isMultiple={true}
                                            files={files}
                                            disabled={isProcessing}
                                            icon={<UploadCloud size={24} className="text-blue-500" />}
                                        />
                                        <div className="flex justify-end">
                                            <button
                                                type="button"
                                                onClick={handleExtractData}
                                                disabled={files.length === 0 || !files.some(f => f.type === 'application/pdf') || isProcessing}
                                                className="btn-secondary flex items-center justify-center text-xs py-1.5"
                                            >
                                                {isExtracting ? <Loader className="animate-spin mr-2" size={14} /> : <FileCog size={14} className="mr-2" />}
                                                {isExtracting ? 'Extraindo...' : 'Extrair Dados'}
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    formData.attachments && formData.attachments.length > 0 && (
                                        <div className="space-y-2">
                                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Arquivos Anexados</h4>
                                            <ul className="space-y-2">
                                                {formData.attachments.map((file, index) => (
                                                    <li key={index} className="flex justify-between items-center bg-white dark:bg-slate-800 p-2 rounded border border-slate-200 dark:border-slate-700 shadow-sm">
                                                        <div className="flex items-center gap-2 overflow-hidden">
                                                            <FileText size={16} className="text-slate-400 flex-shrink-0" />
                                                            <span className="truncate text-sm font-medium" title={file.name}>{file.name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => handleDownloadClick(file, `file-${index}`)} className="p-1 text-blue-500 hover:bg-blue-50 dark:hover:bg-slate-700 rounded transition-colors">
                                                                {downloading === `file-${index}` ? <Loader className="animate-spin" size={16} /> : (file.name && file.name.toLowerCase().endsWith('.pdf') ? <Eye size={16} /> : <Download size={16} />)}
                                                            </button>
                                                            {!isReadOnly && (
                                                                <button onClick={() => handleRemoveAttachment(file)} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors">
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )
                                )}
                            </div>

                            {/* Main Form Fields */}
                            <form className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div><label htmlFor="notificationNumber" className={labelStyle}>Número da Notificação</label><input type="text" id="notificationNumber" name="notificationNumber" value={formData.notificationNumber || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} required /></div>
                                    <div><label htmlFor="assignedTechnician" className={labelStyle}>Técnico Atribuído</label>
                                        <select id="assignedTechnician" name="assignedTechnician" value={formData.assignedTechnician || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly}>
                                            <option value="">Nenhum</option>
                                            {(usersList || []).filter(u => u.role === 'tecnico' || u.role === 'tecnico_campo').map(user => (
                                                <option key={user.id} value={user.id}>{user.displayName}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-1">
                                        <label htmlFor="cep" className={labelStyle}>CEP</label>
                                        <div className="relative">
                                            <input type="text" id="cep" name="cep" value={formData.cep || ''} onChange={handleChange} className={`${inputStyle} pr-8`} disabled={isReadOnly} />
                                            <button type="button" onClick={handleCepSearch} disabled={isReadOnly || isSearchingCep} className="absolute right-1 top-1/2 transform -translate-y-1/2 p-1.5 text-slate-400">
                                                {isSearchingCep ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-span-2"><label htmlFor="address" className={labelStyle}>Endereço</label><input type="text" id="address" name="address" value={formData.address || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} /></div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div><label htmlFor="neighborhood" className={labelStyle}>Bairro</label><input type="text" id="neighborhood" name="neighborhood" value={formData.neighborhood || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} /></div>
                                    <div><label htmlFor="city" className={labelStyle}>Cidade</label><select id="city" name="city" value={formData.city || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly}>{(cities || []).map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}</select></div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-2"><label htmlFor="coordinates" className={labelStyle}>Coordenadas</label><input type="text" id="coordinates" name="coordinates" value={`${formData.coordinates.lat || ''}, ${formData.coordinates.lng || ''}`} onChange={(e) => { const [lat, lng] = e.target.value.split(','); setFormData(prev => ({ ...prev, coordinates: { lat: lat.trim(), lng: (lng || '').trim() } })) }} className={inputStyle} disabled={isReadOnly} /></div>
                                    <div><label htmlFor="poleQuantity" className={labelStyle}>Qtd. Postes</label><input type="number" id="poleQuantity" name="poleQuantity" value={formData.poleQuantity || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} /></div>
                                </div>

                                <div className="grid grid-cols-12 gap-4">
                                    <div className="col-span-5"><label htmlFor="issueDate" className={labelStyle}>Emissão</label><input type="date" id="issueDate" name="issueDate" value={formData.issueDate || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} /></div>
                                    <div className="col-span-2"><label htmlFor="deadlineDays" className={labelStyle}>Prazo</label><input type="number" id="deadlineDays" name="deadlineDays" value={formData.deadlineDays || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} /></div>
                                    <div className="col-span-5"><label htmlFor="dueDate" className={labelStyle}>Vencimento</label><input type="date" id="dueDate" name="dueDate" value={formData.dueDate || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} required /></div>
                                </div>

                                <div><label htmlFor="status" className={labelStyle}>Status</label>
                                    <select id="status" name="status" value={formData.status || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly}>
                                        <option value={STATUS.PENDENTE}>Pendente</option>
                                        <option value={STATUS.EM_EXECUCAO}>Em execução</option>
                                        <option value={STATUS.FINALIZADA}>Finalizada</option>
                                    </select>
                                </div>

                                <div><label htmlFor="description" className={labelStyle}>Irregularidade Identificada</label><textarea id="description" name="description" value={formData.description || ''} onChange={handleChange} rows="3" className={inputStyle} disabled={isReadOnly}></textarea></div>
                            </form>
                        </div>

                        {/* Mobile Accordion Header for Map */}
                        <div
                            className="md:hidden flex justify-between items-center bg-slate-100 dark:bg-slate-700/50 p-3 rounded-lg cursor-pointer mt-4"
                            onClick={() => toggleSection('map')}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedSections.map}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSection('map')}
                        >
                            <h3 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2"><MapIcon size={18} /> Mapa</h3>
                            {expandedSections.map ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>

                        <div className={`${!expandedSections.map && 'hidden'} md:block pt-4 md:pt-0`}>
                            <h3 className="hidden md:block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Localização no Mapa</h3>
                            <OzmapDisplay
                                notification={formData}
                                darkMapEnabled={isMapDark}
                                isVisible={expandedSections.map || true} // On desktop always visible
                                ozmapData={ozmapData}
                                isLoading={ozmapLoading}
                                error={ozmapError}
                            />
                        </div>
                    </div>


                    {/* --- COLUMN 2: PHOTOS & ACTIONS (RIGHT) --- */}
                    <div className="flex flex-col md:h-full bg-slate-50 dark:bg-slate-900/30 md:overflow-hidden relative">

                        {/* Photos Header / Dropzone */}
                        {canManagePhotos && notification?.id && notification?.status !== STATUS.FINALIZADA ? (
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm z-10 sticky top-0 md:relative">
                                <button
                                    type="button"
                                    onClick={() => photoInputRef.current?.click()}
                                    disabled={isTakingPhoto}
                                    className="w-full flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-xl bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all group cursor-pointer"
                                >
                                    {isTakingPhoto ? (
                                        <Loader className="animate-spin text-blue-500 mb-2" size={32} />
                                    ) : (
                                        <Camera className="text-blue-500 mb-2 group-hover:scale-110 transition-transform" size={32} />
                                    )}
                                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                                        {isTakingPhoto ? (photoProgressMsg || 'Processando...') : 'Adicionar Nova Foto'}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 hidden md:block">
                                        Clique para capturar ou selecionar. Com marca d'água automática.
                                    </span>
                                </button>
                                <input type="file" ref={photoInputRef} className="hidden" accept="image/*" capture="environment" onChange={handlePhotoCapture} />
                            </div>
                        ) : (
                             !notification?.id && (
                                <div className="p-8 text-center text-slate-500">
                                    <Info className="mx-auto mb-2" />
                                    <p>Salve a notificação para habilitar o envio de fotos.</p>
                                </div>
                            )
                        )}

                        {/* Photos Grid - Scrollable Area */}
                        <div className="p-4 pb-24 md:flex-1 md:overflow-y-auto md:pb-40"> {/* Padding bottom extra for mobile FAB/Sticky footer */}
                            {formData.photos && formData.photos.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {formData.photos.map((photo, index) => {
                                        const canDeletePhoto = userRole === 'administrador' || userRole === 'tecnico' || (userRole === 'tecnico_campo' && photo.createdBy === currentUser?.uid);
                                        return (
                                            <div key={index} className="relative group rounded-lg overflow-hidden shadow-md aspect-square bg-slate-200 dark:bg-slate-700">
                                                <ImageWithLoader
                                                    primarySrc={photo.thumbnailURL}
                                                    fallbackSrc={photo.url}
                                                    alt={`Foto ${index + 1}`}
                                                    className="w-full h-full object-cover pointer-events-none"
                                                />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-20 pointer-events-none">
                                                    <button onClick={(e) => { e.stopPropagation(); setZoomedPhoto({ photo, index, canDeletePhoto }); }} className="p-2 bg-white/20 hover:bg-white/40 rounded-full text-white backdrop-blur-sm pointer-events-auto focus:outline-none focus:ring-2 focus:ring-blue-500" title="Visualizar foto" aria-label={`Visualizar foto ${index + 1}`}>
                                                        <Eye size={20} />
                                                    </button>
                                                    <button onClick={(e) => { e.stopPropagation(); handleDownloadClick(photo, `photo-dl-${index}`); }} className="p-2 bg-white/20 hover:bg-white/40 rounded-full text-white backdrop-blur-sm pointer-events-auto focus:outline-none focus:ring-2 focus:ring-blue-500" title="Baixar foto" aria-label="Baixar foto">
                                                        {downloading === `photo-dl-${index}` ? <Loader className="animate-spin" size={20} /> : <Download size={20} />}
                                                    </button>
                                                    {canDeletePhoto && (
                                                        <button onClick={(e) => {
                                                            e.stopPropagation();
                                                            setConfirmationDetails({
                                                                isOpen: true,
                                                                title: 'Excluir Foto',
                                                                message: 'Deseja realmente excluir esta foto?',
                                                                onConfirm: () => onDeletePhoto(notification.id, photo)
                                                            });
                                                        }} className="p-2 bg-red-500/80 hover:bg-red-600 rounded-full text-white backdrop-blur-sm pointer-events-auto focus:outline-none focus:ring-2 focus:ring-red-500" title="Excluir foto" aria-label="Excluir foto">
                                                            <Trash2 size={20} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                canManagePhotos && notification?.id && (
                                    <div className="h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 opacity-60">
                                        <ImageIcon size={48} className="mb-2" />
                                        <p>Nenhuma foto ainda</p>
                                    </div>
                                )
                            )}
                        </div>

                        {/* --- ACTIONS STICKY FOOTER (RIGHT COL) --- */}
                        {/* Show only if notification exists and user can manage */}
                        {notification?.id && (
                            <div className="sticky bottom-0 md:absolute md:bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-20">
                                {notification?.status !== STATUS.FINALIZADA && canManagePhotos ? (
                                    <div className="space-y-4">
                                        {/* Toggle Sem Rede */}
                                        <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/30 p-2 rounded-lg">
                                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                                <WifiOff size={16} /> Rede Ausente
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setIsNetworkless(!isNetworkless)}
                                                className={`${isNetworkless ? 'bg-red-500' : 'bg-slate-300 dark:bg-slate-600'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2`}
                                                role="switch"
                                                aria-checked={isNetworkless}
                                                aria-label="Alternar status Sem Rede"
                                            >
                                                <span className={`${isNetworkless ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}></span>
                                            </button>
                                        </div>

                                        {/* Main Close Button */}
                                        <button
                                            onClick={() => setShowClosureDialog(true)}
                                            disabled={isProcessing}
                                            className="w-full btn-primary bg-green-600 hover:bg-green-700 py-3 text-base flex items-center justify-center shadow-lg"
                                        >
                                            {isProcessing ? <Loader className="animate-spin mr-2" /> : <Check className="mr-2" />}
                                            Encerrar Notificação
                                        </button>
                                    </div>
                                ) : (
                                    /* Actions for Finalized or Read-Only */
                                    <div className="space-y-3">
                                        {userRole === 'administrador' && notification?.status === STATUS.FINALIZADA && notification?.responseStatus !== 'Respondida' && (
                                            (notification.isNetworkless || (!formData.photos || formData.photos.length === 0)) ? (
                                                <button onClick={handleMarkAsAnswered} disabled={isMarkingAnswered || isProcessing} className="w-full btn-secondary bg-blue-500 hover:bg-blue-600 text-white flex justify-center items-center">
                                                    {isMarkingAnswered ? <Loader className="animate-spin mr-2" /> : <Check className="mr-2" />}
                                                    Marcar como Respondida
                                                </button>
                                            ) : (
                                                <button onClick={handleGeneratePdf} disabled={isGeneratingPdf || isProcessing} className="w-full btn-secondary flex justify-center items-center">
                                                    {isGeneratingPdf ? <Loader className="animate-spin mr-2" /> : <FileText className="mr-2" />}
                                                    Gerar Relatório PDF
                                                </button>
                                            )
                                        )}
                                        {userRole === 'administrador' && notification?.status === STATUS.FINALIZADA && notification?.responseStatus === 'Respondida' && (!notification.isNetworkless && (formData.photos && formData.photos.length > 0)) && (
                                            <button onClick={handleGeneratePdf} disabled={isGeneratingPdf || isProcessing} className="w-full btn-secondary flex justify-center items-center">
                                                {isGeneratingPdf ? <Loader className="animate-spin mr-2" /> : <FileText className="mr-2" />}
                                                Gerar Relatório PDF
                                            </button>
                                        )}
                                        {notification?.status === STATUS.FINALIZADA && (
                                            <div className="text-center p-2 bg-green-50 dark:bg-green-900/20 rounded text-green-700 dark:text-green-300 text-sm font-semibold flex items-center justify-center gap-2">
                                                <Check size={16} /> Notificação Finalizada
                                            </div>
                                        )}
                                        {/* Se não for atribuído ao user atual, mas user é tecnico/admin e está pendente */}
                                        {notification?.status === STATUS.PENDENTE && !isAssignedToCurrentUser && ['tecnico', 'administrador', 'tecnico_campo'].includes(userRole) && (
                                            <button onClick={handleAssign} disabled={isProcessing} className="w-full btn-primary">
                                                Assumir Notificação
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                </div>

                {/* --- MOBILE FOOTER SAVE BAR --- */}
                <div className="md:hidden border-t border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800 flex items-center justify-between shrink-0">
                    <button onClick={onClose} className="btn-secondary">Cancelar</button>
                    {canEdit && (
                        <button onClick={handleSubmit} disabled={isProcessing} className="btn-primary flex items-center">
                            {isProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                            Salvar
                        </button>
                    )}
                </div>

                        {/* --- ZOOMED PHOTO OVERLAY --- */}
                        {zoomedPhoto && (
                            <div className="absolute inset-0 bg-white dark:bg-slate-800 z-50 flex flex-col animate-in fade-in zoom-in-95 duration-200">
                                {/* Header */}
                                <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 shrink-0">
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                        <ImageIcon size={20} className="text-blue-500" /> Visualização Ampliada
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleDownloadClick(zoomedPhoto.photo, `photo-zoom-dl-${zoomedPhoto.index}`)}
                                            disabled={downloading === `photo-zoom-dl-${zoomedPhoto.index}`}
                                            className="btn-secondary py-1.5 px-3 flex items-center gap-2"
                                            title="Baixar foto"
                                            aria-label="Baixar foto"
                                        >
                                            {downloading === `photo-zoom-dl-${zoomedPhoto.index}` ? <Loader className="animate-spin" size={16} /> : <Download size={16} />}
                                            <span className="hidden sm:inline">Baixar</span>
                                        </button>
                                        {zoomedPhoto.canDeletePhoto && (
                                            <button
                                                onClick={() => {
                                                    setZoomedPhoto(null);
                                                    setConfirmationDetails({
                                                        isOpen: true,
                                                        title: 'Excluir Foto',
                                                        message: 'Deseja realmente excluir esta foto?',
                                                        onConfirm: () => onDeletePhoto(notification.id, zoomedPhoto.photo)
                                                    });
                                                }}
                                                disabled={isProcessing}
                                                className="py-1.5 px-3 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 rounded-lg transition-colors flex items-center gap-2"
                                                title="Excluir foto"
                                                aria-label="Excluir foto"
                                            >
                                                <Trash2 size={16} />
                                                <span className="hidden sm:inline">Excluir</span>
                                            </button>
                                        )}
                                        <button onClick={() => setZoomedPhoto(null)} className="p-2 ml-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500" title="Fechar visualização">
                                            <X size={20} className="text-slate-600 dark:text-slate-300" />
                                        </button>
                                    </div>
                                </div>
                                {/* Body */}
                                <div className="flex-1 overflow-hidden bg-slate-100 dark:bg-slate-900/50 p-4 flex items-center justify-center cursor-grab active:cursor-grabbing">
                                    <TransformWrapper
                                        initialScale={1}
                                        minScale={0.5}
                                        maxScale={4}
                                        centerOnInit={true}
                                        wheel={{ step: 0.1 }}
                                        pinch={{ step: 5 }}
                                    >
                                        <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center">
                                            <ImageWithLoader
                                                primarySrc={zoomedPhoto.photo.url}
                                                fallbackSrc={zoomedPhoto.photo.thumbnailURL}
                                                alt={`Foto ampliada`}
                                                className="w-full h-full rounded-lg shadow-inner bg-transparent dark:bg-transparent pointer-events-none"
                                                imgClassName="object-contain pointer-events-none"
                                            />
                                        </TransformComponent>
                                    </TransformWrapper>
                                </div>
                            </div>
                        )}

            </div>
        </div>
    );
};

export default NewNotificationModal;
